import React, { useState, useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Upload, Download, MessageCircle, Mail, Filter, X, Check, UserCheck } from 'lucide-react';
import { downloadExport } from '../utils/exportUtils';
import { buildWaUrl, WA_TEMPLATES } from '../utils/whatsapp';
import { fillItrChecklist, deriveYearsFromAy } from '../utils/itrChecklist';
import ChallanTab from '../components/ChallanTab';
import { internalDueColor } from '../utils/dueDate';
import api from '../utils/api';
import usePersistedFilter from '../utils/usePersistedFilter';
import { fmtDate, fmtCurrency, AY_OPTIONS, CURRENT_AY, statusColor, canDelete, canImport } from '../utils/helpers';
import { PageHeader, SearchBar, Modal, FormField } from '../components/Table';
import DueDateEditModal from '../components/DueDateEditModal';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import ConfirmModal from '../components/ConfirmModal';
import ModuleImport from '../components/ModuleImport';
import { patchRows } from '../components/gridShared';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ── Constants ──────────────────────────────────────────────────────────────────
const ITR_STATUSES = ['Data Pending', 'Data Received', 'Computation Done', 'Reviewed', 'Filed', 'Acknowledged'];
const ITR_FORMS = ['ITR-1', 'ITR-2', 'ITR-3', 'ITR-4', 'ITR-5', 'ITR-6', 'ITR-7'];
const FILING_TYPES = ['Original', 'Revised', 'Belated'];
const NOTICE_TYPES = ['143(1) Intimation', '143(2) Scrutiny', '148 Reassessment', '156 Demand', '271 Penalty', 'Others'];
const NOTICE_STATUSES = ['Received', 'Response Drafted', 'Submitted', 'Hearing Scheduled', 'Order Received', 'Appeal Filed', 'Resolved'];
const ENTITY_TYPES = ['Individual', 'HUF', 'Firm', 'LLP', 'Pvt Ltd', 'Ltd', 'Trust', 'AOP', 'BOI', 'Society'];

// FIX 2/5: act_email replaces act_pdf
const ALL_COLS = [
  'client_code', 'client_name', 'entity_type',
  'ay', 'itr_form', 'filing_type', 'primary_assignee_id', 'status',
  'effective_due_date', 'date_filed', 'udin', 'internal_due_date',
  'act_edit', 'act_email', 'act_whatsapp', 'act_delete',
];
const DROPDOWN_COLS = new Set(['ay', 'itr_form', 'filing_type', 'primary_assignee_id', 'status']);
const DATE_COLS = new Set(['date_filed', 'internal_due_date']);
const READONLY_COLS = new Set(['client_code', 'client_name', 'entity_type', 'effective_due_date']);
const ACTION_COLS = new Set(['act_edit', 'act_email', 'act_whatsapp', 'act_delete']);

function today3() {
  const d = new Date(); d.setDate(d.getDate() + 3);
  return d.toISOString().split('T')[0];
}
function freshEntry() {
  return { client_id: null, client_code: '', client_name: '', entity_type: '',
    ay: CURRENT_AY, itr_form: 'ITR-1', filing_type: 'Original',
    status: 'Data Pending', primary_assignee_id: '',
    date_filed: '', udin: '', internal_due_date: today3(), is_audit_case: false };
}

// ── Column-filter helpers ──────────────────────────────────────────────────────
function filterMatches(row, colFilters) {
  for (const [col, f] of Object.entries(colFilters)) {
    if (!f) continue;
    if (Array.isArray(f)) { if (f.length && !f.includes(String(row[col] ?? ''))) return false; }
    else if (typeof f === 'object') { const v = row[col] ?? ''; if (f.from && v < f.from) return false; if (f.to && v > f.to) return false; }
    else if (!String(row[col] ?? '').toLowerCase().includes(f.toLowerCase())) return false;
  }
  return true;
}
function countActive(colFilters) {
  return Object.values(colFilters).filter(f => Array.isArray(f) ? f.length : (f && typeof f === 'object' ? f.from || f.to : !!f)).length;
}

// ── FIX 1: WhatsApp message modal ─────────────────────────────────────────────
// `templates`: optional [{ label, msg }] — a picker swaps the message body.
// {{client_name}} in the message is substituted per recipient at send time.
function WhatsAppModal({ targets, defaultMsg, templates, onClose }) {
  // targets: [{ name, whatsapp }], single or bulk
  const isBulk = targets.length > 1;
  const [msg, setMsg] = useState(defaultMsg || '');
  const [tpl, setTpl] = useState('');
  const textRef = useRef();
  useEffect(() => { textRef.current?.focus(); textRef.current?.select(); }, []);

  const pickTemplate = label => {
    setTpl(label);
    const t = templates?.find(x => x.label === label);
    if (t) setMsg(t.msg);
    else if (label === '') setMsg(defaultMsg || '');
  };

  const sendAll = () => {
    targets.forEach((t, i) => {
      if (!t.whatsapp) return;
      const personal = msg.replaceAll('{{client_name}}', t.name || 'Sir/Madam');
      const url = buildWaUrl(t.whatsapp, personal);
      if (url) setTimeout(() => window.open(url, '_blank'), i * 400);
    });
    onClose();
  };

  const withNumber = targets.filter(t => t.whatsapp);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle size={20} className="text-green-600" />
          <h3 className="font-semibold text-gray-900">
            {isBulk ? `WhatsApp — ${withNumber.length} of ${targets.length} clients have numbers` : `WhatsApp — ${targets[0]?.name}`}
          </h3>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {isBulk && (
          <div className="mb-3 max-h-28 overflow-y-auto border border-gray-100 rounded-lg divide-y">
            {targets.map((t, i) => (
              <div key={i} className={`px-3 py-1.5 text-xs flex items-center gap-2 ${t.whatsapp ? '' : 'opacity-40'}`}>
                <span className="font-medium text-gray-700 flex-1">{t.name}</span>
                <span className="text-gray-400 font-mono">{t.whatsapp || 'No number'}</span>
              </div>
            ))}
          </div>
        )}
        {!isBulk && targets[0]?.whatsapp && (
          <p className="text-xs text-gray-500 mb-3">+91 {targets[0].whatsapp}</p>
        )}
        {templates?.length > 0 && (
          <div className="mb-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Template</label>
            <select value={tpl} onChange={e => pickTemplate(e.target.value)} className="input-field text-sm">
              <option value="">Status update (default)</option>
              {templates.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
            </select>
          </div>
        )}
        <label className="block text-xs font-medium text-gray-700 mb-1">Message{isBulk ? ' ({{client_name}} fills per recipient)' : ''}</label>
        <textarea ref={textRef} value={msg} onChange={e => setMsg(e.target.value)} rows={7}
          className="input-field text-sm w-full resize-y font-normal leading-relaxed mb-4" />
        <div className="flex gap-3">
          <button onClick={sendAll} disabled={withNumber.length === 0}
            className="flex-1 py-2 rounded-lg font-medium text-sm bg-green-600 hover:bg-green-700 text-white disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
            <MessageCircle size={14} />
            {isBulk ? `Send to ${withNumber.length} client${withNumber.length !== 1 ? 's' : ''}` : 'Send via WhatsApp'}
          </button>
          <button onClick={onClose} className="flex-1 py-2 rounded-lg font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── FIX 2: Email compose modal ─────────────────────────────────────────────────
// `templates`: optional [{ label, subject, body }] — picker swaps subject+body.
function EmailModal({ targets, defaultSubject, defaultBody, templates, onClose }) {
  const isBulk = targets.length > 1;
  const [subject, setSubject] = useState(defaultSubject || '');
  const [body, setBody] = useState(defaultBody || '');
  const [tpl, setTpl] = useState('');

  const pickTemplate = label => {
    setTpl(label);
    const t = templates?.find(x => x.label === label);
    if (t) { setSubject(t.subject); setBody(t.body); }
    else if (label === '') { setSubject(defaultSubject || ''); setBody(defaultBody || ''); }
  };

  const sendAll = () => {
    targets.forEach((t, i) => {
      if (!t.email) return;
      const personalBody = body.replace(/\[Client Name\]/g, t.name).replaceAll('{{client_name}}', t.name);
      const link = `mailto:${t.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(personalBody)}`;
      setTimeout(() => window.open(link, '_blank'), i * 300);
    });
    onClose();
  };

  const withEmail = targets.filter(t => t.email);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <Mail size={20} className="text-[#1e3a5f]" />
          <h3 className="font-semibold text-gray-900">
            {isBulk ? `Email — ${withEmail.length} of ${targets.length} clients` : `Email — ${targets[0]?.name}`}
          </h3>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {isBulk && (
          <div className="mb-3 max-h-24 overflow-y-auto border border-gray-100 rounded-lg divide-y">
            {targets.map((t, i) => (
              <div key={i} className={`px-3 py-1.5 text-xs flex items-center gap-2 ${t.email ? '' : 'opacity-40'}`}>
                <span className="font-medium text-gray-700 flex-1">{t.name}</span>
                <span className="text-gray-400">{t.email || 'No email'}</span>
              </div>
            ))}
          </div>
        )}
        {!isBulk && <p className="text-xs text-gray-500 mb-3">{targets[0]?.email || 'No email on file'}</p>}
        {templates?.length > 0 && (
          <FormField label="Template">
            <select value={tpl} onChange={e => pickTemplate(e.target.value)} className="input-field text-sm">
              <option value="">Status update (default)</option>
              {templates.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
            </select>
          </FormField>
        )}
        <FormField label="Subject">
          <input value={subject} onChange={e => setSubject(e.target.value)} className="input-field text-sm" />
        </FormField>
        <FormField label={isBulk ? 'Message (use [Client Name] as placeholder)' : 'Message'}>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={7}
            className="input-field text-sm w-full resize-y font-normal leading-relaxed" />
        </FormField>
        <div className="flex gap-3 mt-4">
          <button onClick={sendAll} disabled={withEmail.length === 0}
            className="flex-1 py-2 rounded-lg font-medium text-sm bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 text-white disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
            <Mail size={14} />
            {isBulk ? `Open email for ${withEmail.length} client${withEmail.length !== 1 ? 's' : ''}` : 'Open Email Client'}
          </button>
          <button onClick={onClose} className="flex-1 py-2 rounded-lg font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── FIX 6: Quick Add Client mini-modal ────────────────────────────────────────
function QuickAddClientModal({ initialName, onCreated, onClose }) {
  const [form, setForm] = useState({ client_code: '', name: initialName || '', entity_type: 'Individual', mobile: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const r = await api.post('/clients', {
        name: form.name.trim(),
        client_code: form.client_code.trim() || null,
        entity_type: form.entity_type,
        mobile: form.mobile.trim() || null,
        whatsapp: form.mobile.trim() || null,
        services: '[]',
        status: 'Active',
      });
      const newClient = { id: r.data.id, name: form.name.trim(), client_code: form.client_code.trim() || null, entity_type: form.entity_type };
      toast.success(`Client "${form.name}" added`);
      onCreated(newClient);
    } catch (e) { setError(e?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Plus size={16} className="text-[#1e3a5f]" /> Quick Add Client</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <p className="text-xs text-gray-400 mb-4">Creates the client immediately. Complete full details in Client Master later.</p>
        <div className="space-y-3">
          <FormField label="Client Code"><input value={form.client_code} onChange={e => setForm(f => ({ ...f, client_code: e.target.value }))} className="input-field" placeholder="e.g. KUA-C011" /></FormField>
          <FormField label="Client Name *"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input-field" autoFocus /></FormField>
          <FormField label="Entity Type">
            <select value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))} className="input-field">
              {ENTITY_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </FormField>
          <FormField label="Mobile (optional)"><input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} className="input-field" placeholder="WhatsApp-linked number" /></FormField>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={save} disabled={saving} className="flex-1 btn-primary text-sm py-2">{saving ? 'Adding…' : 'Add Client'}</button>
          <button onClick={onClose} className="flex-1 btn-secondary text-sm py-2">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function IncomeTaxPage() {
  const { user, can } = useAuth();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState('itr');
  const [data, setData] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [search, setSearch] = useState('');
  const [ayFilter, setAyFilter] = usePersistedFilter('pms_filter_ay_itr');
  // Default view hides Acknowledged (finished + acknowledged work). Persisted
  // per user; '__active__' only applies on the ITR tab (notices ignore it).
  const [statusFilter, setStatusFilter] = usePersistedFilter('pms_filter_status_itr', '__active__');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [dupWarning, setDupWarning] = useState(null);
  const [dueDateRecord, setDueDateRecord] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const clientFilter = sp.get('client') || '';

  const [colFilters, setColFilters] = useState({});
  const [filterDropdown, setFilterDropdown] = useState(null);

  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // FIX 1 & 2: message modal state
  const [waModal, setWaModal] = useState(null);    // { targets, defaultMsg }
  const [emailModal, setEmailModal] = useState(null); // { targets, subject, body }
  // Firm contact for template signatures — settings keys firm_phone/firm_email
  // (falls back to the SMTP sender), same settings source other templates use
  const [firmContact, setFirmContact] = useState({ phone: '', email: '' });
  useEffect(() => {
    api.get('/settings').then(r => setFirmContact({
      phone: r.data.firm_phone || '',
      email: r.data.firm_email || r.data.smtp_email || '',
    })).catch(() => {});
  }, []);

  // 'ITR Document Checklist' template rows for the WA/Email modals
  const itrChecklistWa = (clientName, ay) => ({
    label: 'ITR Document Checklist',
    msg: fillItrChecklist({ clientName, ay, firmPhone: firmContact.phone, firmEmail: firmContact.email }),
  });
  const itrChecklistEmail = (clientName, ay) => ({
    label: 'ITR Document Checklist',
    subject: `Documents required for your Income Tax Return — AY ${deriveYearsFromAy(ay).ay || ''}`,
    body: fillItrChecklist({ clientName, ay, firmPhone: firmContact.phone, firmEmail: firmContact.email }),
  });

  const canDel = canDelete(user);
  const canImp = canImport(user);

  const loadData = useCallback(() => {
    const params = {};
    if (ayFilter) params.ay = ayFilter; // '' = All Years
    if (clientFilter) params.client_id = clientFilter;
    if (statusFilter && !(tab !== 'itr' && statusFilter === '__active__')) params.status = statusFilter;
    if (showArchived) params.show_archived = '1';
    api.get(`/income-tax/${tab === 'itr' ? 'itr' : 'notices'}`, { params })
      .then(r => { setData(r.data); setSelected(new Set()); }).catch(() => {});
  }, [tab, ayFilter, statusFilter, clientFilter, showArchived]);

  const reloadClients = useCallback(() => {
    api.get('/clients').then(r => setClientList(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { api.get('/auth/users').then(r => setStaff(r.data)).catch(() => {}); }, []);
  useEffect(() => { reloadClients(); }, [reloadClients]);
  // Clients created anywhere (e.g. Alt+C quick-add) become searchable in the entry row immediately
  useEffect(() => {
    window.addEventListener('pms:clients-changed', reloadClients);
    return () => window.removeEventListener('pms:clients-changed', reloadClients);
  }, [reloadClients]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (force = false) => {
    const savedForm = { ...form };
    const savedEditing = editing;
    setShowModal(false); setDupWarning(null); setForm({}); setEditing(null);
    try {
      if (tab === 'itr') {
        if (savedEditing) { await api.put(`/income-tax/itr/${savedEditing.id}`, savedForm); toast.success('ITR record updated'); }
        else { await api.post('/income-tax/itr', { ...savedForm, force }); toast.success('ITR record added'); }
      } else {
        if (savedEditing) { await api.put(`/income-tax/notices/${savedEditing.id}`, savedForm); toast.success('Notice updated'); }
        else { await api.post('/income-tax/notices', savedForm); toast.success('Notice saved'); }
      }
      loadData();
    } catch (e) {
      if (e?.response?.status === 409) { setForm(savedForm); setEditing(savedEditing); setDupWarning(e.response.data?.error); setShowModal(true); return; }
      toast.error(e?.response?.data?.error || 'Save failed');
      setForm(savedForm); setEditing(savedEditing); setShowModal(true);
    }
  };

  const handleInlineUpdate = useCallback(async (id, updates) => {
    const row = data.find(r => r.id === id);
    if (!row) throw new Error('Row not found');
    await api.put(`/income-tax/itr/${id}`, { ...row, ...updates });
    // patchRows re-derives primary_assignee_name from the id — the grid shows
    // the joined name, so patching only the id left the old name visible (#F2)
    // In the default Active view, a record saved as Acknowledged leaves the
    // list right away (it's still there under "All Statuses" — nothing is
    // archived or deleted, this is purely the view filter).
    if (updates.status === 'Acknowledged' && statusFilter === '__active__') {
      setData(prev => prev.filter(r => r.id !== id));
      toast.success('Acknowledged — moved out of the Active view (see All Statuses)');
    } else {
      setData(prev => patchRows(prev, id, updates, { staff }));
    }
  }, [data, staff, statusFilter]);

  const handleEntryRowSave = useCallback(async (entryData) => {
    await api.post('/income-tax/itr', {
      client_id: entryData.client_id, ay: entryData.ay,
      itr_form: entryData.itr_form, filing_type: entryData.filing_type,
      status: entryData.status, primary_assignee_id: entryData.primary_assignee_id || null,
      date_filed: entryData.date_filed || null, udin: entryData.udin || null,
      internal_due_date: entryData.internal_due_date || null,
      is_audit_case: entryData.is_audit_case ? 1 : 0,
    });
    loadData();
  }, [loadData]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(tab === 'itr' ? `/income-tax/itr/${deleteTarget.id}` : `/income-tax/notices/${deleteTarget.id}`);
      toast.success('Record deleted'); setDeleteTarget(null); loadData();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const toggleSelect = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = rows => setSelected(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));

  const handleBulkUpdate = async (action, value) => {
    const ids = Array.from(selected);
    try {
      const updates = action === 'status' ? { status: value } : { primary_assignee_id: value === '__UNASSIGN__' ? null : value };
      await api.post('/income-tax/itr/bulk-update', { ids, updates });
      toast.success(action === 'status' ? 'Status updated' : 'Reassigned');
      if (updates.status === 'Acknowledged' && statusFilter === '__active__') {
        const idSet = new Set(ids.map(Number));
        setData(prev => prev.filter(r => !idSet.has(Number(r.id))));
        toast.success(`${ids.length} record${ids.length !== 1 ? 's' : ''} moved out of the Active view (see All Statuses)`);
      } else {
        setData(prev => patchRows(prev, ids, updates, { staff }));
      }
      setShowBulkConfirm(false); setBulkAction(''); setBulkValue(''); setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.error || 'Bulk action failed'); }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    try {
      await api.post('/income-tax/itr/bulk-delete', { ids });
      toast.success(`Deleted ${ids.length} record(s)`);
      setBulkDeleteConfirm(false); setSelected(new Set()); loadData();
    } catch (e) { toast.error(e?.response?.data?.error || 'Bulk delete failed'); }
  };

  const handleExportSelected = () => {
    const rows = displayData.filter(r => selected.has(r.id));
    const headers = ['Client', 'Code', 'AY', 'Form', 'Type', 'Status', 'Filed On', 'UDIN', 'Internal Due'];
    const csv = [headers.join(','), ...rows.map(r => [r.client_name, r.client_code, r.ay, r.itr_form, r.filing_type, r.status, r.date_filed, r.udin, r.internal_due_date].map(v => `"${v || ''}"`).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    Object.assign(document.createElement('a'), { href: url, download: `KUA_ITR_${new Date().toISOString().split('T')[0]}.csv` }).click();
    URL.revokeObjectURL(url);
  };

  // FIX 1: Bulk WhatsApp
  const openBulkWA = () => {
    const rows = displayData.filter(r => selected.has(r.id));
    setWaModal({
      templates: [itrChecklistWa(null, ayFilter || rows[0]?.ay)],
      targets: rows.map(r => ({ name: r.client_name, whatsapp: r.whatsapp })),
      defaultMsg: `Dear [Client Name],\n\nThis is a reminder from Krishna Uday & Associates regarding your ITR filing.\n\nPlease contact us for updates.\n\nThank you.\nKrishna Uday & Associates`,
    });
  };

  // FIX 2: Bulk Email
  const openBulkEmail = () => {
    const rows = displayData.filter(r => selected.has(r.id));
    setEmailModal({
      templates: [itrChecklistEmail(null, ayFilter || rows[0]?.ay)],
      targets: rows.map(r => ({ name: r.client_name, email: r.email })),
      defaultSubject: `ITR Filing Update — ${ayFilter}`,
      defaultBody: `Dear [Client Name],\n\nThis is regarding your ITR filing for ${ayFilter}.\n\nPlease contact us for any queries or to share documents.\n\nRegards,\nKrishna Uday & Associates`,
    });
  };

  const openColFilter = (col, btn) => {
    if (filterDropdown?.col === col) { setFilterDropdown(null); return; }
    const r = btn.getBoundingClientRect();
    setFilterDropdown({ col, x: Math.min(r.left, window.innerWidth - 250), y: Math.min(r.bottom + 4, window.innerHeight - 330) });
  };
  const setColFilter = (col, val) => setColFilters(f => ({ ...f, [col]: val }));
  const clearColFilter = col => setColFilters(f => { const n = { ...f }; delete n[col]; return n; });

  const displayData = useMemo(() => data.filter(d => {
    if (search) { const s = search.toLowerCase(); if (!d.client_name?.toLowerCase().includes(s) && !d.client_code?.toLowerCase().includes(s)) return false; }
    return filterMatches(d, colFilters);
  }), [data, search, colFilters]);

  const colUniqueValues = useMemo(() => {
    const cols = ['entity_type', 'ay', 'itr_form', 'filing_type', 'primary_assignee_name', 'status'];
    return Object.fromEntries(cols.map(c => [c, [...new Set(data.map(r => String(r[c] || '')).filter(Boolean))].sort()]));
  }, [data]);

  // FIX 7: Compute bulk confirm message/variant based on current bulkAction
  const bulkConfirmProps = useMemo(() => {
    if (bulkAction === 'status') {
      return { variant: 'update', message: `Update status to "${bulkValue}" for ${selected.size} selected record${selected.size !== 1 ? 's' : ''}?`, confirmLabel: 'Yes, Update' };
    }
    if (bulkValue === '__UNASSIGN__') {
      return { variant: 'danger', message: `Remove assignee from ${selected.size} record${selected.size !== 1 ? 's' : ''}?`, confirmLabel: 'Yes, Remove' };
    }
    const staffName = staff.find(s => String(s.id) === String(bulkValue))?.name || bulkValue;
    return { variant: 'assign', message: `Assign ${selected.size} record${selected.size !== 1 ? 's' : ''} to "${staffName}"?`, confirmLabel: 'Yes, Assign' };
  }, [bulkAction, bulkValue, selected.size, staff]);

  return (
    <div>
      <PageHeader title="Income Tax" subtitle="ITR Filing & Notices tracker"
        actions={
          <div className="flex gap-2">
            <button onClick={() => downloadExport(tab === 'itr' ? '/export/itr' : '/export/itr-notices', { ay: ayFilter }, `KUA_IT_${tab}_${new Date().toISOString().split('T')[0]}.xlsx`)}
              className="btn-secondary flex items-center gap-2 text-sm"><Download size={15} /> Export</button>
            {canImp && tab === 'itr' && <button onClick={() => setShowImport(true)} className="btn-secondary flex items-center gap-2 text-sm"><Upload size={15} /> Import</button>}
            <button onClick={() => { setEditing(null); setForm({ ay: CURRENT_AY, status: 'Data Pending', filing_type: 'Original', itr_form: 'ITR-1', is_audit_case: false, internal_due_date: today3() }); setDupWarning(null); setShowModal(true); }}
              className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Record</button>
          </div>
        } />

      <div className="flex gap-1 mb-4 flex-wrap">
        {[['itr', 'ITR Filings'], ['notices', 'Notices & Demands'], ['challans', 'Challans']].map(([t, l]) => (
          <button key={t} onClick={() => { setTab(t); setSelected(new Set()); setColFilters({}); setFilterDropdown(null); }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === t ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{l}</button>
        ))}
      </div>

      {tab === 'challans' && <ChallanTab module="itr" />}

      {tab !== 'challans' && (
        <div className="card">
          <div className="flex flex-wrap gap-3 mb-3">
            <SearchBar value={search} onChange={setSearch} placeholder="Search client name or code..." />
            <select value={ayFilter} onChange={e => setAyFilter(e.target.value)} className="input-field w-36">
              <option value="">All Years</option>
              {AY_OPTIONS.map(a => <option key={a}>{a}</option>)}
            </select>
            <select value={tab !== 'itr' && statusFilter === '__active__' ? '' : statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-44">
              {tab === 'itr' && <option value="__active__">Active (hides Acknowledged)</option>}
              <option value="">All Statuses</option>
              {(tab === 'itr' ? ITR_STATUSES : NOTICE_STATUSES).map(s => <option key={s}>{s}</option>)}
            </select>
            {countActive(colFilters) > 0 && (
              <button onClick={() => setColFilters({})} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 border border-red-200 rounded-lg">
                <X size={12} /> Clear {countActive(colFilters)} filter{countActive(colFilters) > 1 ? 's' : ''}
              </button>
            )}
            <button onClick={() => setShowArchived(a => !a)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors flex items-center gap-1 ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}>
              {showArchived ? '📂 Archived' : '📁 Show Archived'}
            </button>
            <span className="ml-auto text-xs text-gray-400 flex items-center">{displayData.length} records</span>
          </div>

          {tab === 'itr' && selected.size > 0 && can('income_tax', 'bulk') && (
            <div className="mb-3 flex flex-wrap items-center gap-2 p-3 bg-[#1e3a5f]/5 border border-[#1e3a5f]/20 rounded-xl">
              <span className="text-sm font-semibold text-[#1e3a5f]">{selected.size} selected</span>
              <div className="flex flex-wrap gap-2 ml-2">
                {/* Status */}
                <div className="flex items-center gap-1">
                  <select value={bulkAction === 'status' ? bulkValue : ''} onChange={e => { setBulkAction('status'); setBulkValue(e.target.value); }} className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
                    <option value="">Update Status →</option>{ITR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {bulkAction === 'status' && bulkValue && <button onClick={() => setShowBulkConfirm(true)} className="px-3 py-1.5 bg-[#1e3a5f] text-white text-xs rounded-lg">Apply</button>}
                </div>
                {/* Assign */}
                <div className="flex items-center gap-1">
                  <select value={bulkAction === 'assignee' ? bulkValue : ''} onChange={e => { setBulkAction('assignee'); setBulkValue(e.target.value); }} className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
                    <option value="">Assign To →</option>
                    <option value="__UNASSIGN__">— Unassigned —</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {bulkAction === 'assignee' && bulkValue && <button onClick={() => setShowBulkConfirm(true)} className="px-3 py-1.5 bg-[#1e3a5f] text-white text-xs rounded-lg">Apply</button>}
                </div>
                {/* FIX 1: Bulk WhatsApp */}
                <button onClick={openBulkWA} className="flex items-center gap-1 px-3 py-1.5 border border-green-300 bg-green-50 text-green-700 rounded-lg text-xs hover:bg-green-100">
                  <MessageCircle size={13} /> WhatsApp
                </button>
                {/* FIX 2: Bulk Email */}
                <button onClick={openBulkEmail} className="flex items-center gap-1 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-xs hover:bg-blue-100">
                  <Mail size={13} /> Email
                </button>
                <button onClick={handleExportSelected} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 bg-white rounded-lg text-xs"><Download size={13} /> Export</button>
                {canDel && <button onClick={() => setBulkDeleteConfirm(true)} className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs"><Trash2 size={13} /> Delete ({selected.size})</button>}
              </div>
              <button onClick={() => setSelected(new Set())} className="ml-auto text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
          )}

          <div className="overflow-x-auto">
            {tab === 'itr' ? (
              <ITRTable
                data={displayData}
                staff={staff}
                clientList={clientList}
                selected={selected}
                toggleSelect={toggleSelect}
                toggleAll={toggleAll}
                onEdit={row => { setEditing(row); setForm({ ...row, is_audit_case: !!row.is_audit_case }); setDupWarning(null); setShowModal(true); }}
                onInlineUpdate={handleInlineUpdate}
                onEntryRowSave={handleEntryRowSave}
                onDelete={canDel ? setDeleteTarget : null}
                onArchive={async (row) => { await api.patch(`/income-tax/itr/${row.id}/${row.is_archived ? 'unarchive' : 'archive'}`); toast.success(row.is_archived ? 'Unarchived' : 'Archived'); loadData(); }}
                onSetDueDate={setDueDateRecord}
                colFilters={colFilters}
                colUniqueValues={colUniqueValues}
                openColFilter={openColFilter}
                onWA={row => setWaModal({ templates: [itrChecklistWa(row.client_name, row.ay)], targets: [{ name: row.client_name, whatsapp: row.whatsapp }], defaultMsg: WA_TEMPLATES.completion(row.client_name, `ITR ${row.itr_form || ''}`, row.ay, row.ack_number) })}
                onEmail={row => setEmailModal({ templates: [itrChecklistEmail(row.client_name, row.ay)], targets: [{ name: row.client_name, email: row.email }], defaultSubject: `ITR Filing Update - AY ${row.ay} - ${row.client_name}`, defaultBody: `Dear ${row.client_name},\n\nThis is regarding your ITR filing for AY ${row.ay}. Current status: ${row.status}.\n\nPlease contact us for any queries.\n\nRegards,\nKrishna Uday & Associates` })}
                onClientCreated={newClient => { reloadClients(); }}
              />
            ) : (
              <NoticesTable data={displayData} onUpdate={async (id, u) => { await api.put(`/income-tax/notices/${id}`, u); loadData(); }} onDelete={canDel ? setDeleteTarget : null} onEdit={row => { setEditing(row); setForm(row); setShowModal(true); }} />
            )}
          </div>
          {tab === 'itr' && (
            <p className="text-xs text-gray-400 mt-2 italic">
              Arrows ↑↓←→ navigate · Alt+↓ opens dropdown instantly · F2 edits · digit on date starts edit · Enter moves down · Esc deselects
            </p>
          )}
        </div>
      )}

      {filterDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setFilterDropdown(null)} />
          <ColFilterDropdown col={filterDropdown.col} x={filterDropdown.x} y={filterDropdown.y}
            data={data} colFilters={colFilters} colUniqueValues={colUniqueValues}
            setColFilter={setColFilter} clearColFilter={clearColFilter} onClose={() => setFilterDropdown(null)} />
        </>
      )}

      {showModal && (
        <Modal title={tab === 'itr' ? (editing ? 'Edit ITR Filing' : 'Add ITR Filing') : 'Add Notice'}
          onClose={() => { setShowModal(false); setEditing(null); setDupWarning(null); }}>
          <form onSubmit={e => { e.preventDefault(); handleSave(false); }} className="space-y-4">
            {!editing && <FormField label="Client" required><ClientSearchDropdown value={form.client_id || ''} initialLabel="" service="IT Returns" onChange={id => setForm(f => ({ ...f, client_id: id }))} required /></FormField>}
            {dupWarning && (
              <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 text-sm text-orange-700">
                <p className="font-semibold mb-1">⚠ Duplicate</p><p>{dupWarning}</p>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => handleSave(true)} className="px-4 py-1.5 bg-orange-600 text-white rounded-lg text-xs">Add Anyway</button>
                  <button type="button" onClick={() => setDupWarning(null)} className="px-4 py-1.5 border border-orange-300 text-orange-700 rounded-lg text-xs">Cancel</button>
                </div>
              </div>
            )}
            {tab === 'itr' ? (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="AY"><select value={form.ay || CURRENT_AY} onChange={e => setForm(f => ({ ...f, ay: e.target.value }))} className="input-field">{AY_OPTIONS.map(a => <option key={a}>{a}</option>)}</select></FormField>
                <FormField label="ITR Form"><select value={form.itr_form || 'ITR-1'} onChange={e => setForm(f => ({ ...f, itr_form: e.target.value }))} className="input-field">{ITR_FORMS.map(x => <option key={x}>{x}</option>)}</select></FormField>
                <FormField label="Filing Type"><select value={form.filing_type || 'Original'} onChange={e => setForm(f => ({ ...f, filing_type: e.target.value }))} className="input-field">{FILING_TYPES.map(x => <option key={x}>{x}</option>)}</select></FormField>
                <FormField label="Status"><select value={form.status || ''} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="input-field">{ITR_STATUSES.map(s => <option key={s}>{s}</option>)}</select></FormField>
                <FormField label="Date Filed"><input type="date" value={form.date_filed || ''} onChange={e => setForm(f => ({ ...f, date_filed: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Refund (₹)"><input type="number" value={form.refund_amount || ''} onChange={e => setForm(f => ({ ...f, refund_amount: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Primary Assignee">
                  <select value={form.primary_assignee_id || ''} onChange={e => setForm(f => ({ ...f, primary_assignee_id: e.target.value }))} className="input-field">
                    <option value="">Not assigned</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Audit Case?">
                  <label className="flex items-center gap-2 cursor-pointer mt-1">
                    <input type="checkbox" checked={!!form.is_audit_case} onChange={e => setForm(f => ({ ...f, is_audit_case: e.target.checked }))} className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-700">Tax Audit (due Oct 31)</span>
                  </label>
                </FormField>
                {!!form.is_audit_case && (
                  <FormField label="UDIN"><input value={form.udin || ''} onChange={e => setForm(f => ({ ...f, udin: e.target.value }))} className="input-field font-mono" placeholder="18-digit UDIN" maxLength={18} /></FormField>
                )}
                <FormField label="Internal Due Date" className="col-span-2">
                  <div className="flex gap-2">
                    <input type="date" value={form.internal_due_date || ''} onChange={e => setForm(f => ({ ...f, internal_due_date: e.target.value }))} className="input-field flex-1" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, internal_due_date: today3() }))} className="text-xs border border-gray-200 rounded px-2 py-1.5 text-[#1e3a5f] hover:text-[#f0a500] whitespace-nowrap">Today+3</button>
                  </div>
                </FormField>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Notice Type"><select value={form.notice_type || ''} onChange={e => setForm(f => ({ ...f, notice_type: e.target.value }))} className="input-field"><option value="">Select</option>{NOTICE_TYPES.map(t => <option key={t}>{t}</option>)}</select></FormField>
                <FormField label="AY"><select value={form.ay || ''} onChange={e => setForm(f => ({ ...f, ay: e.target.value }))} className="input-field"><option value="">Select</option>{AY_OPTIONS.map(a => <option key={a}>{a}</option>)}</select></FormField>
                <FormField label="Date Received"><input type="date" value={form.date_received || ''} onChange={e => setForm(f => ({ ...f, date_received: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Response Due"><input type="date" value={form.response_due_date || ''} onChange={e => setForm(f => ({ ...f, response_due_date: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Demand (₹)"><input type="number" value={form.demand_amount || ''} onChange={e => setForm(f => ({ ...f, demand_amount: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Status"><select value={form.status || 'Received'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="input-field">{NOTICE_STATUSES.map(s => <option key={s}>{s}</option>)}</select></FormField>
                <FormField label="Assigned To"><select value={form.assigned_to || ''} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value ? parseInt(e.target.value) : null }))} className="input-field"><option value="">— Unassigned —</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></FormField>
              </div>
            )}
            <FormField label="Notes"><textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="input-field" rows={2} /></FormField>
            {!dupWarning && <div className="flex gap-3"><button type="submit" className="btn-primary flex-1">Save</button><button type="button" onClick={() => { setShowModal(false); setEditing(null); setDupWarning(null); }} className="btn-secondary flex-1">Cancel</button></div>}
          </form>
        </Modal>
      )}

      {/* FIX 7: Action-specific bulk confirm dialogs */}
      {showBulkConfirm && (
        <ConfirmModal
          variant={bulkConfirmProps.variant}
          message={bulkConfirmProps.message}
          confirmLabel={bulkConfirmProps.confirmLabel}
          onConfirm={() => handleBulkUpdate(bulkAction, bulkValue)}
          onCancel={() => setShowBulkConfirm(false)}
        />
      )}
      {bulkDeleteConfirm && (
        <ConfirmModal
          variant="delete"
          message={`Delete ${selected.size} ITR record${selected.size !== 1 ? 's' : ''}? This cannot be undone.`}
          onConfirm={handleBulkDelete}
          onCancel={() => setBulkDeleteConfirm(false)}
        />
      )}

      {dueDateRecord && <DueDateEditModal record={dueDateRecord} tableName="itr_filings" moduleName="Income Tax" onClose={() => setDueDateRecord(null)} onSaved={loadData} />}
      {deleteTarget && <ConfirmModal variant="delete" message={`Delete this ${tab === 'itr' ? 'ITR filing' : 'notice'}?`} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />}
      {showImport && <ModuleImport module="itr" onComplete={loadData} onClose={() => setShowImport(false)} />}

      {/* FIX 1: WhatsApp modal */}
      {waModal && <WhatsAppModal targets={waModal.targets} defaultMsg={waModal.defaultMsg} templates={waModal.templates} onClose={() => setWaModal(null)} />}
      {/* FIX 2: Email modal */}
      {emailModal && <EmailModal targets={emailModal.targets} defaultSubject={emailModal.defaultSubject} defaultBody={emailModal.defaultBody} templates={emailModal.templates} onClose={() => setEmailModal(null)} />}
    </div>
  );
}

// ── Column filter dropdown (position:fixed) ────────────────────────────────────
function ColFilterDropdown({ col, x, y, data, colFilters, colUniqueValues, setColFilter, clearColFilter, onClose }) {
  const DATE_COLS_SET = new Set(['effective_due_date', 'date_filed', 'internal_due_date']);
  const TEXT_SEARCH_COLS = new Set(['client_name', 'client_code']);
  const isDate = DATE_COLS_SET.has(col);
  const isText = TEXT_SEARCH_COLS.has(col);
  const filter = colFilters[col];
  const uniqueVals = colUniqueValues[col] || [...new Set(data.map(r => String(r[col] || '')).filter(Boolean))].sort();
  const LABELS = { entity_type: 'Entity Type', ay: 'AY', itr_form: 'Form', filing_type: 'Filing Type', primary_assignee_name: 'Assigned To', status: 'Status', effective_due_date: 'Due Date', date_filed: 'Filed On', internal_due_date: 'Internal Due', client_name: 'Client' };
  const hasFilter = Array.isArray(filter) ? filter.length > 0 : !!(filter?.from || filter?.to || filter);

  const toggleVal = val => {
    const cur = Array.isArray(filter) ? filter : [];
    setColFilter(col, cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val]);
  };

  return (
    <div style={{ position: 'fixed', left: x, top: y, zIndex: 50, minWidth: 220, maxWidth: 280 }}
      className="bg-white border border-gray-200 rounded-xl shadow-2xl p-3" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-700">{LABELS[col] || col}</span>
        <div className="flex gap-1">
          {hasFilter && <button onClick={() => clearColFilter(col)} className="text-xs text-red-500 px-1.5 hover:text-red-700">Clear</button>}
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
        </div>
      </div>
      {!isDate && !isText && (
        <div className="max-h-52 overflow-y-auto space-y-0.5">
          {uniqueVals.map(val => {
            const checked = Array.isArray(filter) && filter.includes(val);
            return (
              <div key={val} onClick={() => toggleVal(val)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm ${checked ? 'bg-[#1e3a5f]/5 text-[#1e3a5f]' : 'hover:bg-gray-50 text-gray-700'}`}>
                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${checked ? 'bg-[#1e3a5f] border-[#1e3a5f]' : 'border-gray-300'}`}>
                  {checked && <Check size={10} className="text-white" />}
                </div>
                <span className="truncate">{val || '(blank)'}</span>
              </div>
            );
          })}
        </div>
      )}
      {isDate && (
        <div className="space-y-2">
          <div><label className="text-xs text-gray-500 block mb-0.5">From</label><input type="date" value={filter?.from || ''} onChange={e => setColFilter(col, { ...(filter || {}), from: e.target.value })} className="input-field text-xs py-1.5" /></div>
          <div><label className="text-xs text-gray-500 block mb-0.5">To</label><input type="date" value={filter?.to || ''} onChange={e => setColFilter(col, { ...(filter || {}), to: e.target.value })} className="input-field text-xs py-1.5" /></div>
        </div>
      )}
      {isText && <input type="text" value={filter || ''} onChange={e => setColFilter(col, e.target.value)} placeholder="Search..." autoFocus className="input-field text-sm" />}
    </div>
  );
}

// ── Filterable header cell ─────────────────────────────────────────────────────
function FilterTh({ col, label, sortKey, sortDir, onSort, colFilters, openColFilter }) {
  const f = colFilters[col];
  const count = Array.isArray(f) ? f.length : 0;
  const hasFilter = count > 0 || !!(f?.from || f?.to || (typeof f === 'string' && f));
  return (
    <th className="table-header whitespace-nowrap">
      <div className="flex items-center gap-1 group">
        <span className="cursor-pointer select-none" onClick={() => onSort(col)}>{label}{sortKey === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</span>
        <button onClick={e => { e.stopPropagation(); openColFilter(col, e.currentTarget); }}
          className={`relative flex-shrink-0 p-0.5 rounded ${hasFilter ? 'text-[#1e3a5f]' : 'text-gray-200 group-hover:text-gray-400'}`}>
          <Filter size={11} />
          {count > 0 && <span className="absolute -top-1 -right-1 bg-[#f0a500] text-white text-[8px] w-3 h-3 rounded-full flex items-center justify-center font-bold">{count}</span>}
          {hasFilter && !count && <span className="absolute -top-0.5 -right-0.5 bg-[#1e3a5f] w-1.5 h-1.5 rounded-full" />}
        </button>
      </div>
    </th>
  );
}

// ── Inline dropdown (Alt+Down) ─────────────────────────────────────────────────
function InlineDropdown({ options, current, onSelect, onCancel }) {
  const opts = options.map(o => typeof o === 'object' ? o : { value: String(o), label: String(o) });
  const [hi, setHi] = useState(() => Math.max(0, opts.findIndex(o => o.value === String(current ?? ''))));
  const containerRef = useRef();
  const listRef = useRef();

  useEffect(() => { containerRef.current?.focus(); }, []);
  useEffect(() => { listRef.current?.children[hi]?.scrollIntoView({ block: 'nearest' }); }, [hi]);

  const handleKeyDown = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); setHi(i => Math.min(i + 1, opts.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); setHi(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); e.stopPropagation(); onSelect(opts[hi]?.value ?? ''); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
  };

  return (
    <div ref={containerRef} tabIndex={0} onKeyDown={handleKeyDown}
      onBlur={e => { if (!containerRef.current?.contains(e.relatedTarget)) onCancel(); }}
      className="outline-none">
      <div className="text-xs text-[#1e3a5f] font-medium px-1 py-0.5 border-b border-blue-100 truncate min-h-[1.25rem]">
        {opts[hi]?.label || '—'}
      </div>
      <div ref={listRef} className="absolute left-0 top-full z-[60] bg-white border border-blue-300 rounded-b-xl shadow-2xl min-w-full max-h-48 overflow-y-auto">
        {opts.map((o, i) => (
          <div key={o.value} onMouseDown={e => { e.preventDefault(); onSelect(o.value); }}
            className={`px-3 py-1.5 text-xs cursor-pointer ${i === hi ? 'bg-[#1e3a5f] text-white' : 'hover:bg-blue-50 text-gray-700'}`}>
            {o.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Editable cell ──────────────────────────────────────────────────────────────
function EditableCell({ row, col, displayValue, editOptions, isActive, isEditing, onActivate, onStartEdit, onCommit, onCancel }) {
  const ref = useRef();
  const [localVal, setLocalVal] = useState('');
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (isEditing) {
      setLocalVal(String(row[col] ?? ''));
      if (editOptions?.type !== 'select') {
        ref.current?.focus(); ref.current?.select?.();
      }
    }
  }, [isEditing]);

  const doCommit = async val => {
    if (String(val ?? '') === String(row[col] ?? '')) { onCancel(); return; }
    try { await onCommit(col, val); setFlash('ok'); setTimeout(() => setFlash(null), 700); }
    catch { setFlash('err'); setTimeout(() => setFlash(null), 1000); }
  };

  const kd = e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); } };

  const cellCls = `table-cell cursor-pointer outline-none transition-colors relative
    ${isEditing ? 'ring-2 ring-inset ring-blue-500 bg-blue-50' : isActive ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}
    ${flash === 'ok' ? 'bg-green-50' : flash === 'err' ? 'bg-red-50' : ''}`;

  // data-cell on every path — the scroll-into-view effect targets it, and the
  // original FIX 3 pass missed these editable cells (only static/action tds
  // got the attribute, so arrow-nav didn't scroll on editable columns)
  if (isEditing && editOptions?.type === 'select') {
    return <td data-cell={`${row.id}-${col}`} className={cellCls}><InlineDropdown options={editOptions.options} current={localVal} onSelect={val => { setLocalVal(val); doCommit(val); }} onCancel={onCancel} /></td>;
  }
  if (isEditing && editOptions?.type === 'date') {
    return <td data-cell={`${row.id}-${col}`} className={cellCls}><input ref={ref} type="date" value={localVal} autoFocus onChange={e => setLocalVal(e.target.value)} onBlur={e => doCommit(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); doCommit(localVal); } kd(e); }} className="w-full text-xs bg-transparent border-0 outline-none p-0" /></td>;
  }
  if (isEditing && editOptions?.type === 'text') {
    return <td data-cell={`${row.id}-${col}`} className={cellCls}><input ref={ref} type="text" value={localVal} autoFocus onChange={e => setLocalVal(e.target.value)} onBlur={() => doCommit(localVal)} onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); doCommit(localVal); } kd(e); }} className="w-full text-xs font-mono bg-transparent border-0 outline-none p-0" /></td>;
  }

  return (
    <td data-cell={`${row.id}-${col}`} className={cellCls} tabIndex={0} onClick={onActivate} onDoubleClick={onStartEdit}
      onKeyDown={e => { if ((e.key === 'Enter' || e.key === 'F2') && !READONLY_COLS.has(col)) { e.preventDefault(); onStartEdit(); } }}>
      {flash === 'ok' ? <span className="text-green-600 text-xs flex items-center gap-0.5"><Check size={10} /> OK</span> : displayValue}
    </td>
  );
}

// ── Entry Row — FIX 4 (no setTimeout), FIX 6 (Quick Add Client) ───────────────
const ENTRY_COLS = ['client_code', 'client_name', 'ay', 'itr_form', 'filing_type', 'primary_assignee_id', 'status', 'date_filed', 'udin', 'internal_due_date'];

// Hoisted to module scope (#30): previously defined inside EntryRow, so React
// created a new component type every render and remounted the dropdown on each
// keystroke, losing keyboard focus while navigating suggestions.
const SuggestionsDropdown = React.memo(function SuggestionsDropdown({ field, typed, searchField, suggestions, onPick, onDismiss, onQuickAdd }) {
  const show = searchField === field && (suggestions.length > 0 || typed.trim());
  if (!show) return null;
  return (
    <div className="absolute left-0 top-full mt-0.5 bg-white border border-gray-200 rounded-xl shadow-xl z-50 min-w-64 max-h-52 overflow-y-auto">
      {suggestions.map(c => (
        <button key={c.id} className="suggestion-item w-full text-left px-3 py-2 text-xs hover:bg-blue-50 flex items-center gap-2 focus:bg-blue-50 outline-none"
          onMouseDown={e => { e.preventDefault(); onPick(c); }}
          onKeyDown={e => { if (e.key === 'Enter') onPick(c); if (e.key === 'Escape') onDismiss(field); }}>
          <span className="font-mono text-gray-500 w-20 flex-shrink-0">{c.client_code}</span>
          <span className="font-medium text-gray-800 truncate">{c.name}</span>
          {c.entity_type && <span className="text-gray-400 ml-auto flex-shrink-0">{c.entity_type}</span>}
        </button>
      ))}
      {suggestions.length === 0 && typed.trim() && (
        <button className="w-full text-left px-3 py-2 text-xs text-[#1e3a5f] hover:bg-blue-50 flex items-center gap-2 font-medium border-t border-dashed border-gray-200"
          onMouseDown={e => { e.preventDefault(); onQuickAdd(typed.trim()); }}>
          <Plus size={12} /> Add "{typed.trim()}" as new client
        </button>
      )}
    </div>
  );
});

const EntryRow = forwardRef(function EntryRow({ staff, clientList, onSaved, onFocusUp, onClientCreated }, ref) {
  const [row, setRow] = useState(freshEntry);
  const [activeCol, setActiveCol] = useState(null);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [searchField, setSearchField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [quickAdd, setQuickAdd] = useState(null); // FIX 6: { initialName }
  const rowRef = useRef();
  const codeRef = useRef();
  const nameRef = useRef();

  // FIX 4: no setTimeout — entry row is always mounted, focus directly
  useImperativeHandle(ref, () => ({
    focusCol(col) {
      const entryCol = ENTRY_COLS.includes(col) ? col : ENTRY_COLS[0];
      setActiveCol(entryCol);
      if (entryCol === 'client_code') codeRef.current?.focus();
      else if (entryCol === 'client_name') nameRef.current?.focus();
      else rowRef.current?.querySelector(`[data-entrycol="${entryCol}"]`)?.focus();
    }
  }));

  const searchByCode = val => {
    setRow(r => ({ ...r, client_code: val, client_id: null, client_name: '', entity_type: '' }));
    setSearchField('code');
    if (!val.trim()) { setClientSuggestions([]); return; }
    const q = val.toLowerCase();
    setClientSuggestions(clientList.filter(c => c.client_code?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q)).slice(0, 8));
  };

  const searchByName = val => {
    setRow(r => ({ ...r, client_name: val, client_id: null, client_code: '', entity_type: '' }));
    setSearchField('name');
    if (!val.trim()) { setClientSuggestions([]); return; }
    const q = val.toLowerCase();
    setClientSuggestions(clientList.filter(c => c.name?.toLowerCase().includes(q) || c.client_code?.toLowerCase().includes(q)).slice(0, 8));
  };

  const pickClient = c => {
    setRow(r => ({ ...r, client_id: c.id, client_code: c.client_code || '', client_name: c.name, entity_type: c.entity_type || '' }));
    setClientSuggestions([]);
    setSearchField(null);
    setActiveCol('ay');
    // FIX 4: direct focus, no delay
    rowRef.current?.querySelector('[data-entrycol="ay"]')?.focus();
  };

  // FIX 6: called when quick-add modal saves a new client
  const handleQuickAddCreated = newClient => {
    setQuickAdd(null);
    onClientCreated?.(newClient);
    pickClient(newClient);
  };

  const save = async () => {
    if (!row.client_id || !row.ay || !row.itr_form) { setError('Client, AY and Form are required'); return; }
    setSaving(true); setError('');
    try {
      await onSaved(row);
      toast.success('Record added');
      setRow(freshEntry());
      setActiveCol(null);
      setSearchField(null);
      codeRef.current?.focus();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Save failed';
      if (e?.response?.status === 409) { setError('Duplicate: ' + msg); } else { setError(msg); }
    } finally { setSaving(false); }
  };

  const handleRowBlur = () => {
    setTimeout(() => {
      if (rowRef.current && !rowRef.current.contains(document.activeElement)) {
        if (row.client_id && row.ay && row.itr_form) save();
      }
    }, 150);
  };

  // FIX 4: no setTimeout in tab navigation
  const tabToNext = col => {
    const idx = ENTRY_COLS.indexOf(col);
    if (idx < ENTRY_COLS.length - 1) {
      const nextCol = ENTRY_COLS[idx + 1];
      setActiveCol(nextCol);
      if (nextCol === 'client_name') nameRef.current?.focus();
      else rowRef.current?.querySelector(`[data-entrycol="${nextCol}"]`)?.focus();
    } else { save(); }
  };

  const tabToPrev = col => {
    const idx = ENTRY_COLS.indexOf(col);
    if (idx > 0) {
      const prevCol = ENTRY_COLS[idx - 1];
      setActiveCol(prevCol);
      if (prevCol === 'client_code') codeRef.current?.focus();
      else if (prevCol === 'client_name') nameRef.current?.focus();
      else rowRef.current?.querySelector(`[data-entrycol="${prevCol}"]`)?.focus();
    }
  };

  const tabHandler = (col, e) => {
    if (e.key === 'ArrowUp') {
      const idx = ENTRY_COLS.indexOf(col);
      if (idx === 0) { e.preventDefault(); onFocusUp?.(col); return; }
    }
    if (e.key === 'Tab') { e.preventDefault(); e.shiftKey ? tabToPrev(col) : tabToNext(col); }
    if (e.key === 'Enter') { e.preventDefault(); tabToNext(col); }
    if (e.key === 'Escape') { setRow(freshEntry()); setActiveCol(null); setClientSuggestions([]); setSearchField(null); setError(''); }
  };

  const cellCls = col => `table-cell ${activeCol === col ? 'ring-2 ring-inset ring-emerald-400 bg-emerald-50/40' : ''}`;

  return (
    <>
      <tr ref={rowRef} onBlur={handleRowBlur}
        className="border-b-2 border-dashed border-emerald-300 bg-emerald-50/20">
        <td className="table-cell w-8"><span className="text-emerald-400 text-xs">+</span></td>

        {/* Client Code */}
        <td className={`table-cell relative ${activeCol === 'client_code' ? 'ring-2 ring-inset ring-emerald-400 bg-emerald-50/40' : ''}`}>
          <input ref={codeRef} data-entrycol="client_code" value={row.client_code} placeholder="Code…" autoComplete="off"
            onChange={e => searchByCode(e.target.value)} onFocus={() => setActiveCol('client_code')}
            onKeyDown={e => { if (e.key === 'ArrowDown' && clientSuggestions.length) { e.preventDefault(); rowRef.current?.querySelector('.suggestion-item')?.focus(); return; } tabHandler('client_code', e); }}
            className="w-full text-xs bg-transparent border-0 outline-none placeholder-gray-300 font-mono" />
          <SuggestionsDropdown field="code" typed={row.client_code} searchField={searchField} suggestions={clientSuggestions}
            onPick={pickClient}
            onDismiss={f => { setClientSuggestions([]); (f === 'name' ? nameRef : codeRef).current?.focus(); }}
            onQuickAdd={name => { setClientSuggestions([]); setQuickAdd({ initialName: name }); }} />
        </td>

        {/* Client Name */}
        <td className={`table-cell relative ${activeCol === 'client_name' ? 'ring-2 ring-inset ring-emerald-400 bg-emerald-50/40' : ''}`}>
          {row.client_id ? (
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-gray-700 truncate">{row.client_name}</span>
              <button onMouseDown={e => { e.preventDefault(); setRow(r => ({ ...freshEntry(), ay: r.ay, itr_form: r.itr_form, filing_type: r.filing_type, status: r.status, primary_assignee_id: r.primary_assignee_id, internal_due_date: r.internal_due_date })); setClientSuggestions([]); setSearchField(null); codeRef.current?.focus(); }}
                className="text-gray-300 hover:text-red-400 flex-shrink-0"><X size={10} /></button>
            </div>
          ) : (
            <input ref={nameRef} data-entrycol="client_name" value={row.client_name} placeholder="Name…" autoComplete="off"
              onChange={e => searchByName(e.target.value)} onFocus={() => setActiveCol('client_name')}
              onKeyDown={e => { if (e.key === 'ArrowDown' && clientSuggestions.length) { e.preventDefault(); rowRef.current?.querySelector('.suggestion-item')?.focus(); return; } tabHandler('client_name', e); }}
              className="w-full text-xs bg-transparent border-0 outline-none placeholder-gray-300" />
          )}
          <SuggestionsDropdown field="name" typed={row.client_name} searchField={searchField} suggestions={clientSuggestions}
            onPick={pickClient}
            onDismiss={f => { setClientSuggestions([]); (f === 'name' ? nameRef : codeRef).current?.focus(); }}
            onQuickAdd={name => { setClientSuggestions([]); setQuickAdd({ initialName: name }); }} />
        </td>

        {/* Entity Type */}
        <td className="table-cell">
          {row.entity_type ? <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{row.entity_type}</span> : <span className="text-gray-300 text-xs italic">auto</span>}
        </td>

        {/* AY */}
        <td className={cellCls('ay')}>
          <select data-entrycol="ay" value={row.ay} onChange={e => setRow(r => ({ ...r, ay: e.target.value }))}
            onFocus={() => setActiveCol('ay')} onKeyDown={e => tabHandler('ay', e)}
            className="w-full text-xs bg-transparent border-0 outline-none cursor-pointer p-0">
            {AY_OPTIONS.map(a => <option key={a}>{a}</option>)}
          </select>
        </td>

        {/* Form */}
        <td className={cellCls('itr_form')}>
          <select data-entrycol="itr_form" value={row.itr_form} onChange={e => setRow(r => ({ ...r, itr_form: e.target.value }))}
            onFocus={() => setActiveCol('itr_form')} onKeyDown={e => tabHandler('itr_form', e)}
            className="w-full text-xs bg-transparent border-0 outline-none cursor-pointer p-0">
            {ITR_FORMS.map(x => <option key={x}>{x}</option>)}
          </select>
        </td>

        {/* Filing Type */}
        <td className={cellCls('filing_type')}>
          <select data-entrycol="filing_type" value={row.filing_type} onChange={e => setRow(r => ({ ...r, filing_type: e.target.value }))}
            onFocus={() => setActiveCol('filing_type')} onKeyDown={e => tabHandler('filing_type', e)}
            className="w-full text-xs bg-transparent border-0 outline-none cursor-pointer p-0">
            {FILING_TYPES.map(x => <option key={x}>{x}</option>)}
          </select>
        </td>

        {/* Assignee */}
        <td className={cellCls('primary_assignee_id')}>
          <select data-entrycol="primary_assignee_id" value={row.primary_assignee_id} onChange={e => setRow(r => ({ ...r, primary_assignee_id: e.target.value }))}
            onFocus={() => setActiveCol('primary_assignee_id')} onKeyDown={e => tabHandler('primary_assignee_id', e)}
            className="w-full text-xs bg-transparent border-0 outline-none cursor-pointer p-0">
            <option value="">—</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </td>

        {/* Status */}
        <td className={cellCls('status')}>
          <select data-entrycol="status" value={row.status} onChange={e => setRow(r => ({ ...r, status: e.target.value }))}
            onFocus={() => setActiveCol('status')} onKeyDown={e => tabHandler('status', e)}
            className="w-full text-xs bg-transparent border-0 outline-none cursor-pointer p-0">
            {ITR_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </td>

        {/* Due Date — auto */}
        <td className="table-cell text-gray-300 text-xs italic">auto</td>

        {/* Filed On */}
        <td className={cellCls('date_filed')}>
          <input data-entrycol="date_filed" type="date" value={row.date_filed}
            onChange={e => setRow(r => ({ ...r, date_filed: e.target.value }))}
            onFocus={() => setActiveCol('date_filed')} onKeyDown={e => tabHandler('date_filed', e)}
            className="w-full text-xs bg-transparent border-0 outline-none p-0" />
        </td>

        {/* UDIN */}
        <td className={cellCls('udin')}>
          <input data-entrycol="udin" type="text" value={row.udin} placeholder="—"
            onChange={e => setRow(r => ({ ...r, udin: e.target.value }))}
            onFocus={() => setActiveCol('udin')} onKeyDown={e => tabHandler('udin', e)}
            className="w-full text-xs font-mono bg-transparent border-0 outline-none placeholder-gray-200 p-0" />
        </td>

        {/* Internal Due */}
        <td className={cellCls('internal_due_date')}>
          <input data-entrycol="internal_due_date" type="date" value={row.internal_due_date}
            onChange={e => setRow(r => ({ ...r, internal_due_date: e.target.value }))}
            onFocus={() => setActiveCol('internal_due_date')} onKeyDown={e => tabHandler('internal_due_date', e)}
            className="w-full text-xs bg-transparent border-0 outline-none p-0" />
        </td>

        {/* Save / status — spans 4 action cols */}
        <td className="table-cell" colSpan={4}>
          {error ? <span className="text-xs text-red-500">{error}</span>
            : saving ? <span className="text-xs text-gray-400">Saving…</span>
            : row.client_id && row.ay && row.itr_form
              ? <button onClick={save} className="text-xs text-emerald-600 hover:text-emerald-800 font-medium border border-emerald-300 rounded px-2 py-0.5">Save ↵</button>
              : <span className="text-xs text-gray-300 italic">fill client</span>}
        </td>
      </tr>

      {/* FIX 6: Quick Add Client mini-modal */}
      {quickAdd && <QuickAddClientModal initialName={quickAdd.initialName} onCreated={handleQuickAddCreated} onClose={() => setQuickAdd(null)} />}
    </>
  );
});

// ── ITR Table ──────────────────────────────────────────────────────────────────
function ITRTable({ data, staff, clientList, selected, toggleSelect, toggleAll, onEdit, onInlineUpdate, onEntryRowSave, onDelete, onArchive, onSetDueDate, colFilters, colUniqueValues, openColFilter, onWA, onEmail, onClientCreated }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [activeCell, setActiveCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const tableContainerRef = useRef();
  const entryRowRef = useRef();

  // Alt+I — jump straight to the entry row's first cell
  useEffect(() => {
    const h = () => entryRowRef.current?.focusCol('client_code');
    window.addEventListener('pms:new-record', h);
    return () => window.removeEventListener('pms:new-record', h);
  }, []);

  const onSort = key => { setSortDir(d => sortKey === key ? (d === 'asc' ? 'desc' : 'asc') : 'asc'); setSortKey(key); };

  const sorted = useMemo(() => sortKey ? [...data].sort((a, b) => {
    const va = a[sortKey] ?? ''; const vb = b[sortKey] ?? '';
    return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  }) : data, [data, sortKey, sortDir]);

  const activate = (rowId, col) => { setActiveCell({ rowId, col }); setEditingCell(null); tableContainerRef.current?.focus(); };
  const startEdit = (rowId, col) => { if (READONLY_COLS.has(col) || ACTION_COLS.has(col)) return; setActiveCell({ rowId, col }); setEditingCell({ rowId, col }); };
  const cancelEdit = () => { setEditingCell(null); tableContainerRef.current?.focus(); };
  const commitEdit = async (col, val) => {
    if (!editingCell) return;
    await onInlineUpdate(editingCell.rowId, { [col]: val || null });
    setEditingCell(null);
    setActiveCell({ rowId: editingCell.rowId, col });
    tableContainerRef.current?.focus();
  };

  const handleEntryArrowUp = col => {
    if (sorted.length > 0) {
      const targetCol = ALL_COLS.includes(col) ? col : ALL_COLS[0];
      setActiveCell({ rowId: sorted[sorted.length - 1].id, col: targetCol });
      tableContainerRef.current?.focus();
    }
  };

  // FIX 3: scroll active cell into view
  useEffect(() => {
    if (!activeCell) return;
    const el = tableContainerRef.current?.querySelector(`[data-cell="${activeCell.rowId}-${activeCell.col}"]`);
    // instant, not smooth: smooth falls behind under key-repeat, and grids are animation-free
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeCell]);

  const handleKeyDown = e => {
    if (!activeCell) return;
    if (editingCell) {
      if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
      return;
    }

    const { rowId, col } = activeCell;
    const rowIdx = sorted.findIndex(r => r.id === rowId);
    const colIdx = ALL_COLS.indexOf(col);

    if (e.altKey && e.key === 'ArrowDown') {
      e.preventDefault();
      if (DROPDOWN_COLS.has(col)) startEdit(rowId, col);
      return;
    }
    if (!e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && /^\d$/.test(e.key) && DATE_COLS.has(col)) {
      e.preventDefault(); startEdit(rowId, col); return;
    }

    // FIX 2/5: action col keyboard triggers
    if (ACTION_COLS.has(col) && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      const row = sorted[rowIdx];
      if (!row) return;
      if (col === 'act_edit') onEdit(row);
      else if (col === 'act_email') onEmail(row);
      else if (col === 'act_whatsapp') onWA(row);
      else if (col === 'act_delete') onDelete?.(row);
      return;
    }

    switch (e.key) {
      case 'ArrowRight': e.preventDefault();
        if (colIdx < ALL_COLS.length - 1) setActiveCell({ rowId, col: ALL_COLS[colIdx + 1] });
        else if (rowIdx < sorted.length - 1) setActiveCell({ rowId: sorted[rowIdx + 1].id, col: ALL_COLS[0] });
        break;
      case 'ArrowLeft': e.preventDefault();
        if (colIdx > 0) setActiveCell({ rowId, col: ALL_COLS[colIdx - 1] });
        else if (rowIdx > 0) setActiveCell({ rowId: sorted[rowIdx - 1].id, col: ALL_COLS[ALL_COLS.length - 1] });
        break;
      case 'ArrowDown': e.preventDefault();
        if (rowIdx < sorted.length - 1) setActiveCell({ rowId: sorted[rowIdx + 1].id, col });
        else { setActiveCell(null); entryRowRef.current?.focusCol(col); }
        break;
      case 'ArrowUp': e.preventDefault();
        if (rowIdx > 0) setActiveCell({ rowId: sorted[rowIdx - 1].id, col });
        break;
      case 'Tab': e.preventDefault();
        if (e.shiftKey) {
          if (colIdx > 0) setActiveCell({ rowId, col: ALL_COLS[colIdx - 1] });
          else if (rowIdx > 0) setActiveCell({ rowId: sorted[rowIdx - 1].id, col: ALL_COLS[ALL_COLS.length - 1] });
        } else {
          if (colIdx < ALL_COLS.length - 1) setActiveCell({ rowId, col: ALL_COLS[colIdx + 1] });
          else if (rowIdx < sorted.length - 1) setActiveCell({ rowId: sorted[rowIdx + 1].id, col: ALL_COLS[0] });
        }
        break;
      case 'Enter': e.preventDefault();
        if (rowIdx < sorted.length - 1) setActiveCell({ rowId: sorted[rowIdx + 1].id, col });
        else { setActiveCell(null); entryRowRef.current?.focusCol(col); }
        break;
      case 'F2': e.preventDefault(); startEdit(rowId, col); break;
      case 'Escape': setActiveCell(null); break;
      case ' ': e.preventDefault(); if (!ACTION_COLS.has(col)) toggleSelect(rowId); break;
      default: break;
    }
  };

  const thProps = { sortKey, sortDir, onSort, colFilters, openColFilter };
  const staffOptions = [{ value: '', label: '—' }, ...staff.map(s => ({ value: String(s.id), label: s.name }))];

  const actCls = (row, col) => `table-cell cursor-pointer outline-none px-1 text-center
    ${activeCell?.rowId === row.id && activeCell?.col === col ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`;

  return (
    <div ref={tableContainerRef} tabIndex={0} onKeyDown={handleKeyDown} className="outline-none">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="table-header w-8">
              <input type="checkbox" checked={data.length > 0 && selected.size === data.length} onChange={() => toggleAll(data)} className="rounded" />
            </th>
            <FilterTh col="client_code" label="Code" {...thProps} />
            <FilterTh col="client_name" label="Client" {...thProps} />
            <FilterTh col="entity_type" label="Entity" {...thProps} />
            <FilterTh col="ay" label="AY" {...thProps} />
            <FilterTh col="itr_form" label="Form" {...thProps} />
            <FilterTh col="filing_type" label="Type" {...thProps} />
            <FilterTh col="primary_assignee_name" label="Assigned To" {...thProps} />
            <FilterTh col="status" label="Status" {...thProps} />
            <FilterTh col="effective_due_date" label="Due Date" {...thProps} />
            <FilterTh col="date_filed" label="Filed On" {...thProps} />
            <FilterTh col="udin" label="UDIN" {...thProps} />
            <FilterTh col="internal_due_date" label="Internal Due" {...thProps} />
            <th className="table-header w-8 text-center text-gray-300 font-normal text-xs">Edit</th>
            <th className="table-header w-8 text-center text-gray-300 font-normal text-xs">Email</th>
            <th className="table-header w-8 text-center text-gray-300 font-normal text-xs">WA</th>
            <th className="table-header w-8 text-center text-gray-300 font-normal text-xs">Del</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const isSel = selected.has(row.id);
            const isRowActive = activeCell?.rowId === row.id;
            const isCellActive = col => isRowActive && activeCell?.col === col;
            const isCellEditing = col => editingCell?.rowId === row.id && editingCell?.col === col;
            const rowCls = `border-b border-gray-50 ${isSel ? 'bg-amber-50' : isRowActive ? 'bg-blue-50/20' : 'hover:bg-gray-50/50'}`;

            return (
              <tr key={row.id} className={rowCls}>
                <td className="table-cell w-8">
                  <input type="checkbox" checked={isSel} onChange={() => toggleSelect(row.id)} className="rounded" />
                </td>

                {/* FIX 3: data-cell attr for scrollIntoView */}
                <td data-cell={`${row.id}-client_code`} className={`table-cell ${isCellActive('client_code') ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`}
                  tabIndex={0} onClick={() => activate(row.id, 'client_code')}>
                  <span className="text-xs font-mono text-gray-500">{row.client_code || '—'}</span>
                </td>
                <td data-cell={`${row.id}-client_name`} className={`table-cell ${isCellActive('client_name') ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`}
                  tabIndex={0} onClick={() => activate(row.id, 'client_name')}>
                  <span className="font-medium text-gray-800 text-sm">{row.client_name}</span>
                </td>
                <td data-cell={`${row.id}-entity_type`} className={`table-cell ${isCellActive('entity_type') ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`}
                  tabIndex={0} onClick={() => activate(row.id, 'entity_type')}>
                  {row.entity_type ? <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{row.entity_type}</span> : <span className="text-gray-300">—</span>}
                </td>

                {['ay', 'itr_form', 'filing_type'].map(col => (
                  <EditableCell key={col} row={row} col={col}
                    displayValue={<span className={`text-xs ${col === 'ay' ? 'font-medium' : ''}`}>{row[col] || '—'}</span>}
                    editOptions={{ type: 'select', options: col === 'ay' ? AY_OPTIONS : col === 'itr_form' ? ITR_FORMS : FILING_TYPES }}
                    isActive={isCellActive(col)} isEditing={isCellEditing(col)}
                    onActivate={() => activate(row.id, col)} onStartEdit={() => startEdit(row.id, col)}
                    onCommit={commitEdit} onCancel={cancelEdit} />
                ))}

                <EditableCell row={row} col="primary_assignee_id"
                  displayValue={<span className="text-xs text-gray-600">{row.primary_assignee_name || <span className="text-gray-300">—</span>}</span>}
                  editOptions={{ type: 'select', options: staffOptions }}
                  isActive={isCellActive('primary_assignee_id')} isEditing={isCellEditing('primary_assignee_id')}
                  onActivate={() => activate(row.id, 'primary_assignee_id')} onStartEdit={() => startEdit(row.id, 'primary_assignee_id')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="status"
                  displayValue={<span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(row.status)}`}>{row.status}</span>}
                  editOptions={{ type: 'select', options: ITR_STATUSES }}
                  isActive={isCellActive('status')} isEditing={isCellEditing('status')}
                  onActivate={() => activate(row.id, 'status')} onStartEdit={() => startEdit(row.id, 'status')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <td data-cell={`${row.id}-effective_due_date`} className={`table-cell ${isCellActive('effective_due_date') ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`}
                  tabIndex={0} onClick={() => activate(row.id, 'effective_due_date')}>
                  <div className="flex items-center gap-1">
                    {row.effective_due_date ? <span className={`text-xs font-medium ${row.revised_due_date ? 'text-orange-600' : 'text-gray-700'}`}>{fmtDate(row.effective_due_date)}</span> : <span className="text-gray-300 text-xs">—</span>}
                    {row.revised_due_date && <span className="text-xs text-orange-500">Rev</span>}
                    <button onClick={e => { e.stopPropagation(); onSetDueDate(row); }} className="text-gray-300 hover:text-[#f0a500]"><Pencil size={11} /></button>
                  </div>
                </td>

                <EditableCell row={row} col="date_filed"
                  displayValue={<span className="text-xs">{fmtDate(row.date_filed) || <span className="text-gray-300">—</span>}</span>}
                  editOptions={{ type: 'date' }}
                  isActive={isCellActive('date_filed')} isEditing={isCellEditing('date_filed')}
                  onActivate={() => activate(row.id, 'date_filed')} onStartEdit={() => startEdit(row.id, 'date_filed')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="udin"
                  displayValue={row.is_audit_case ? <span className="text-xs font-mono text-gray-600">{row.udin || <span className="text-amber-500">Pending</span>}</span> : <span className="text-gray-300 text-xs">—</span>}
                  editOptions={row.is_audit_case ? { type: 'text' } : null}
                  isActive={isCellActive('udin')} isEditing={isCellEditing('udin')}
                  onActivate={() => activate(row.id, 'udin')} onStartEdit={() => row.is_audit_case && startEdit(row.id, 'udin')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="internal_due_date"
                  displayValue={row.internal_due_date ? <span className={`text-xs ${internalDueColor(row.internal_due_date, row.status)}`}>{fmtDate(row.internal_due_date)}</span> : <span className="text-gray-300 text-xs">—</span>}
                  editOptions={{ type: 'date' }}
                  isActive={isCellActive('internal_due_date')} isEditing={isCellEditing('internal_due_date')}
                  onActivate={() => activate(row.id, 'internal_due_date')} onStartEdit={() => startEdit(row.id, 'internal_due_date')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                {/* Action cells with data-cell attrs for FIX 3 */}
                <td data-cell={`${row.id}-act_edit`} tabIndex={0} className={actCls(row, 'act_edit')}
                  onClick={() => { activate(row.id, 'act_edit'); onEdit(row); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(row); } }}>
                  <span className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</span>
                </td>

                {/* FIX 2: Email icon replaces PDF */}
                <td data-cell={`${row.id}-act_email`} tabIndex={0} className={actCls(row, 'act_email')}
                  onClick={() => { activate(row.id, 'act_email'); onEmail(row); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEmail(row); } }}>
                  <span className={`flex justify-center ${row.email ? 'text-gray-400 hover:text-[#1e3a5f]' : 'text-gray-200 cursor-not-allowed'}`}><Mail size={14} /></span>
                </td>

                {/* FIX 1: WhatsApp opens modal, not direct send */}
                <td data-cell={`${row.id}-act_whatsapp`} tabIndex={0} className={actCls(row, 'act_whatsapp')}
                  onClick={() => { activate(row.id, 'act_whatsapp'); onWA(row); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onWA(row); } }}>
                  <span className={`flex justify-center ${row.whatsapp ? 'text-gray-400 hover:text-green-500' : 'text-gray-200 cursor-not-allowed'}`}><MessageCircle size={14} /></span>
                </td>

                <td data-cell={`${row.id}-act_delete`} tabIndex={0} className={actCls(row, 'act_delete')}
                  onClick={() => { activate(row.id, 'act_delete'); onDelete?.(row); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDelete?.(row); } }}>
                  {onDelete && <span className="flex justify-center text-gray-300 hover:text-red-500 cursor-pointer"><Trash2 size={14} /></span>}
                </td>
                {onArchive && <td className="table-cell">
                  <button title={row.is_archived ? 'Unarchive' : 'Archive'} onClick={() => onArchive(row)} className={`flex justify-center text-xs px-1.5 py-0.5 rounded ${row.is_archived ? 'text-amber-600 hover:text-amber-800' : 'text-gray-300 hover:text-amber-600'}`}>{row.is_archived ? '↩' : '📁'}</button>
                </td>}
              </tr>
            );
          })}

          <EntryRow
            ref={entryRowRef}
            staff={staff}
            clientList={clientList}
            onSaved={onEntryRowSave}
            onFocusUp={handleEntryArrowUp}
            onClientCreated={onClientCreated}
          />
        </tbody>
      </table>
    </div>
  );
}

// ── Notices Table ──────────────────────────────────────────────────────────────
function NoticesTable({ data, onUpdate, onDelete, onEdit }) {
  if (!data.length) return <div className="text-center py-10 text-gray-400">No notices found.</div>;
  return (
    <table className="w-full text-left">
      <thead><tr className="border-b border-gray-100">{['Client', 'Notice Type', 'AY', 'Assigned To', 'Received', 'Reply Due', 'Demand', 'Status', ''].map(h => <th key={h} className="table-header">{h}</th>)}</tr></thead>
      <tbody>
        {data.map((row, i) => {
          const diff = (new Date(row.response_due_date) - new Date()) / 86400000;
          return (
            <tr key={row.id ?? i} className="border-b border-gray-50 hover:bg-gray-50">
              <td className="table-cell"><div className="font-medium text-sm">{row.client_name}</div>{row.client_code && <div className="text-xs text-gray-400">{row.client_code}</div>}</td>
              <td className="table-cell text-xs">{row.notice_type}</td>
              <td className="table-cell text-xs">{row.ay}</td>
              <td className="table-cell text-xs">{row.assignee_name || <span className="text-gray-300">—</span>}</td>
              <td className="table-cell text-xs">{fmtDate(row.date_received)}</td>
              <td className="table-cell"><span className={`text-xs ${diff < 0 ? 'text-red-600 font-semibold' : diff <= 7 ? 'text-orange-600 font-semibold' : 'text-gray-700'}`}>{fmtDate(row.response_due_date)}</span></td>
              <td className="table-cell text-xs">{fmtCurrency(row.demand_amount)}</td>
              <td className="table-cell">
                <select value={row.status} onChange={e => onUpdate(row.id, { ...row, status: e.target.value })} className={`text-xs rounded-full px-2 py-0.5 border-0 cursor-pointer ${statusColor(row.status)}`}>
                  {NOTICE_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </td>
              <td className="table-cell">
                <div className="flex gap-2 items-center">
                  {onEdit && <button onClick={() => onEdit(row)} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</button>}
                  {onDelete && <button onClick={() => onDelete(row)} className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
